import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn } from '../luna-common';

/** Clamp a number to the inclusive [min, max] range. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Snap a number to the nearest multiple of step, offset from min. */
export function snap(value: number, step: number, min = 0): number {
  if (step <= 0) {
    return value;
  }
  // `+ 0` normalises the -0 that Math.round produces for negatives in (-step/2, 0).
  return min + Math.round((value - min) / step) * step + 0;
}

function toInteger(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(n) ? n : null;
}

@customElement('luna-stepper')
export class LunaStepper extends LitPage {
  @property({ attribute: 'label' }) label = '';
  @property({ attribute: 'helper' }) helper = '';

  private _value = 0;
  private _min = 0;
  private _max = 100;
  private _step = 1;

  @property({ type: Number })
  get value(): number {
    return this._value;
  }

  set value(raw: unknown) {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) {
      devWarn(`luna-stepper: invalid value "${raw}" — ignored`);
      return;
    }
    // Snap to step but do NOT clamp: the display must always mirror the value
    // the host holds (e.g. an out-of-range typed value awaiting validation) —
    // clamping here would silently reset the display while the host keeps the
    // raw value. Only the ± buttons clamp (before committing).
    this._value = snap(n, this._step, this._min);
  }

  @property({ type: Number })
  get min(): number {
    return this._min;
  }

  set min(raw: unknown) {
    const n = toInteger(raw);
    if (n === null) {
      devWarn(`luna-stepper: invalid min "${raw}" — falling back to 0`);
      this._min = 0;
    } else {
      this._min = n;
    }
    this.normalizeBounds();
  }

  @property({ type: Number })
  get max(): number {
    return this._max;
  }

  set max(raw: unknown) {
    const n = toInteger(raw);
    if (n === null) {
      devWarn(`luna-stepper: invalid max "${raw}" — falling back to 100`);
      this._max = 100;
    } else {
      this._max = n;
    }
    this.normalizeBounds();
  }

  @property({ type: Number })
  get step(): number {
    return this._step;
  }

  set step(raw: unknown) {
    const n = toInteger(raw);
    if (n === null || n < 1) {
      devWarn(`luna-stepper: invalid step "${raw}" — falling back to 1`);
      this._step = 1;
    } else {
      this._step = n;
    }
    this._value = clamp(snap(this._value, this._step, this._min), this._min, this._max);
  }

  private normalizeBounds(): void {
    if (this._min >= this._max) {
      devWarn('luna-stepper: min must be < max — falling back to 0/100');
      this._min = 0;
      this._max = 100;
    }
    this._value = clamp(snap(this._value, this._step, this._min), this._min, this._max);
  }

  private decrement(): void {
    if (this._value <= this._min) {
      return;
    }
    this.value = clamp(this._value - this._step, this._min, this._max);
    this.emitChange();
  }

  private increment(): void {
    if (this._value >= this._max) {
      return;
    }
    this.value = clamp(this._value + this._step, this._min, this._max);
    this.emitChange();
  }

  private emitChange(): void {
    this.dispatchEvent(
      new CustomEvent('luna-change', {
        detail: { value: this._value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private filterInput(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    // Digits only, optional leading minus — letters are stripped as you type.
    const filtered = input.value.replace(/[^\d-]/g, '').replace(/(?!^)-/g, '');
    if (filtered !== input.value) {
      input.value = filtered;
    }
  }

  private commitInput(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    const raw = input.value.trim();
    const n = Number(raw);
    if (raw === '' || raw === '-' || !Number.isFinite(n)) {
      input.value = String(this._value); // revert the display to the committed value
      return;
    }

    // Snap to step but do NOT clamp: out-of-range values are committed so the
    // host screen can surface its validation error (the ± buttons stay clamped).
    const committed = snap(n, this._step, this._min);
    if (committed === this._value) {
      input.value = String(committed); // normalise the display (snapped)
      return;
    }

    this._value = committed; // direct assignment + requestUpdate (the setter is snap-only too)
    this.requestUpdate();
    this.emitChange();
  }

  private selectInput(event: Event): void {
    (event.currentTarget as HTMLInputElement).select();
  }

  content() {
    const atMin = this._value <= this._min;
    const atMax = this._value >= this._max;

    return html`
      <div class="luna-stepper">
        <span class="luna-stepper__label">${this.label}</span>
        <div class="luna-stepper__row">
          <button
            type="button"
            class="luna-stepper__btn"
            aria-label="Decrease ${this.label}"
            ?disabled=${atMin}
            @click=${this.decrement}
          >
            −
          </button>
          <input
            class="luna-stepper__value mbw-type-value-stepper"
            type="text"
            inputmode="numeric"
            role="spinbutton"
            aria-label=${this.label || 'value'}
            aria-valuemin=${this._min}
            aria-valuemax=${this._max}
            aria-valuenow=${this._value}
            .value=${String(this._value)}
            @input=${this.filterInput}
            @change=${this.commitInput}
            @focus=${this.selectInput}
          />
          <button
            type="button"
            class="luna-stepper__btn"
            aria-label="Increase ${this.label}"
            ?disabled=${atMax}
            @click=${this.increment}
          >
            +
          </button>
        </div>
        ${this.helper
          ? html`<span class="luna-stepper__helper">${this.helper}</span>`
          : ''}
      </div>
    `;
  }

  styles() {
    return css`
      .luna-stepper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        width: 420px;
        max-width: 100%;
      }

      .luna-stepper__label {
        font-family: var(--mbw-font-sans);
        font-weight: 700;
        font-size: 15px; /* pinned: figma Luna Stepper (no Bold-15 type style) */
        line-height: 20px;
        color: var(--cds-text-primary);
      }

      .luna-stepper__row {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        height: 56px;
      }

      .luna-stepper__btn {
        flex-shrink: 0;
        width: 56px;
        height: 56px;
        box-sizing: border-box;
        border: none;
        border-radius: var(--luna-radius-btn);
        cursor: pointer;
        font-family: var(--mbw-font-sans);
        font-weight: 700;
        font-size: 28px;
        line-height: 1;
        color: var(--luna-outline-checkpoint-text);
        background-image: linear-gradient(180deg, #ffffff 0%, #eef3fb 100%); /* pinned: figma Luna Stepper */
        box-shadow: inset 0 0 0 2px var(--luna-outline-checkpoint),
          0 3px 0 var(--luna-outline-checkpoint-ledge);
      }

      .luna-stepper__btn:active:not(:disabled) {
        box-shadow: inset 0 0 0 2px var(--luna-outline-checkpoint),
          0 1px 0 var(--luna-outline-checkpoint-ledge);
      }

      .luna-stepper__btn:focus-visible {
        outline: none;
        box-shadow:
          inset 0 0 0 2px var(--luna-outline-checkpoint),
          0 3px 0 var(--luna-outline-checkpoint-ledge),
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      .luna-stepper__btn:disabled {
        opacity: 0.4; /* derived disabled treatment (BR-26 / register 6.1) */
        cursor: default;
        box-shadow: inset 0 0 0 2px var(--luna-outline-checkpoint);
      }

      .luna-stepper__value {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 56px;
        box-sizing: border-box;
        border: none;
        border-radius: var(--luna-radius-btn);
        background: var(--luna-surface-field);
        box-shadow: inset 0 0 0 2px #cdd8ec; /* pinned: figma Luna Stepper */
        color: var(--cds-text-primary);
        text-align: center;
        padding: 0 12px;
        appearance: none;
      }

      .luna-stepper__value:focus-visible {
        outline: none;
        box-shadow:
          inset 0 0 0 2px #cdd8ec, /* pinned: figma Luna Stepper */
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      .luna-stepper__helper {
        font-family: var(--mbw-font-sans);
        font-weight: 400;
        font-size: 13px; /* pinned: figma Luna Stepper (no 13px type style) */
        line-height: 17px;
        color: var(--mbw-text-faint);
      }
    `;
  }
}
